import { Alert, Box, Stack, Switch, Typography } from '@mui/material';
import { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowSmallDown } from '@/assets/icons';
import { theme } from '@/modules/common/theme';
import { Accordion } from '@/modules/common/components/Accordion';
import { NumberInput } from '@common/components/inputs';
import { OverflowTypography } from '@common/components/OverflowTypography';
import { PropertiesLabel } from '@common/components/PropertiesLabel';
import { VehicleTypeRange } from '@/modules/simulation/helpers/customTypes';
import { VehiclesUtil } from '@/modules/vehicles/vehiclesUtil';

type Props = {
  vtRange: VehicleTypeRange;
  imageUrl: string;
  onRangeChange: (range: VehicleTypeRange) => void;
};

const VehicleTypeComponent = ({ vtRange, imageUrl, onRangeChange }: Props) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const [activeRange, setRange] = useState<VehicleTypeRange>(vtRange);
  const [isRange, setIsRange] = useState<boolean>(false);
  const [capacity, setCapacity] = useState<number>(vtRange.range.to);
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsRange(vtRange.range.from !== vtRange.range.to);
  }, [vtRange]);

  const toggleOpen = useCallback(() => setOpen((current) => !current), [setOpen]);

  const updateRange = useCallback(
    (newRange: VehicleTypeRange) => {
      setRange(newRange);
      onRangeChange(newRange);
    },
    [onRangeChange],
  );

  const onVehicleRangeToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
      if (checked) {
        updateRange({ ...activeRange, range: { from: 1, to: capacity, step: 1 } });
      } else {
        updateRange({
          ...activeRange,
          range: { from: activeRange.range.to, to: activeRange.range.to, step: 1 },
        });
        setCapacity(activeRange.range.to);
      }
    },
    [capacity, updateRange, activeRange],
  );

  const onCapacityChange = useCallback(
    (count: number) => {
      try {
        if (count > vtRange.maxCapacity) {
          throw new Error(
            `${t(
              'interface:simulation.simulation_edit.vehicle_types.floorplan_capacity',
              'The floorplan capacity for this vehicle is',
            )}: ${capacity}`,
          );
        } else {
          setCapacity(count);
          updateRange({
            ...activeRange,
            range: { from: count, to: count, step: 1 },
          });
        }
      } catch (e) {
        return { error: e.message };
      }
    },
    [vtRange, activeRange, updateRange, t, capacity],
  );

  const onStepChange = useCallback(
    (value: number) => {
      try {
        if (value < 1) throw new Error(`${t('common:minimum_value', { minValue: 1 })}`);

        if (activeRange.range.to - activeRange.range.from < value) {
          const largestValidStep = activeRange.range.to - activeRange.range.from;
          throw new Error(`Maximum ${largestValidStep}. Decrease "From" or increase "To"`);
        }

        updateRange({
          ...activeRange,
          range: {
            ...activeRange.range,
            step: value,
          },
        });
      } catch (e) {
        return { error: e.message };
      }
    },
    [activeRange, updateRange, t],
  );

  const onRangeFromChange = useCallback(
    (value: number) => {
      try {
        if (value < 1) throw new Error(`${t('common:minimum_value', { minValue: 1 })}`);
        if (value > activeRange.range.to - 1)
          throw new Error(`${t('common:maximum_value', { maxValue: activeRange.range.to - 1 })}`);

        if (activeRange.range.to - value < activeRange.range.step) {
          const maximumFrom = activeRange.range.to - 1;
          throw new Error(`Maximum ${maximumFrom}. Either decrease "Step" or increase "To"`);
        }

        updateRange({
          ...activeRange,
          range: {
            ...activeRange.range,
            from: value,
          },
        });
      } catch (e) {
        return { error: e.message };
      }
    },
    [activeRange, updateRange, t],
  );

  const onRangeToChange = useCallback(
    (value: number) => {
      try {
        if (value < 1) throw new Error(`${t('common:minimum_value', { minValue: 1 })}`);
        if (value > activeRange.maxCapacity)
          throw new Error(`${t('common:maximum_value', { maxValue: activeRange.maxCapacity })}`);

        if (value - activeRange.range.from < activeRange.range.step) {
          const minimumTo = activeRange.range.from + activeRange.range.step;
          throw new Error(`Minimum ${minimumTo}. Decrease "From" or "Step"`);
        }

        updateRange({
          ...activeRange,
          range: {
            ...activeRange.range,
            to: value,
          },
        });
      } catch (e) {
        return { error: e.message };
      }
    },
    [activeRange, updateRange, t],
  );

  const accordionTitleComponent = useMemo(
    () => (
      <Stack
        direction='row'
        alignItems='center'
        onClick={toggleOpen}
        justifyContent='space-between'
      >
        <Stack direction='row' alignItems='center' maxWidth='75%' paddingRight={1} gap={0.5}>
          <img src={imageUrl} alt={activeRange.name} width={32} height={32} />
          <OverflowTypography sx={{ flexShrink: 1 }}>{activeRange.name}</OverflowTypography>
        </Stack>
        <Stack sx={{ width: '25%' }}>
          {isRange ? (
            <Typography
              sx={{
                padding: 1,
                fontSize: '12px!important',
                border: '1px solid transparent',
                color: theme.palette.disabled.main,
                background: theme.palette.others.lighter,
                cursor: 'text',
              }}
            >
              {`${activeRange.range.from}-${activeRange.range.to}`}
            </Typography>
          ) : (
            <NumberInput
              ariaLabel={activeRange.name}
              name={`vehicleCount_${activeRange.name}`}
              errorType='floating'
              value={capacity}
              minValue={1}
              onChange={onCapacityChange}
              decimals={0}
            />
          )}
        </Stack>
      </Stack>
    ),
    [imageUrl, isRange, activeRange, capacity, onCapacityChange, toggleOpen],
  );

  if (activeRange.maxCapacity === 0) {
    const isShuttle = VehiclesUtil.isShuttleVehicle(activeRange.name);
    const zeroCapacityMessage = isShuttle
      ? t('interface:simulation.simulation_edit.vehicle_types.zero_capacity_shuttle_subheader')
      : t('interface:simulation.simulation_edit.vehicle_types.zero_capacity_subheader');

    return (
      <Stack>
        <Stack direction='row' alignItems='center' gap={0.5} px={2} py={0.5}>
          <img src={imageUrl} alt={activeRange.name} width={24} height={24} />
          <OverflowTypography sx={{ flexShrink: 1, fontSize: '13px' }}>
            {activeRange.name}
          </OverflowTypography>
        </Stack>
        <Alert severity='info' sx={{ mb: 1 }}>
          {zeroCapacityMessage}
        </Alert>
      </Stack>
    );
  }

  return (
    <Accordion
      ref={containerRef}
      expanded={open}
      onChange={(expanded: boolean) => setOpen(expanded)}
      expandIcon={<ArrowSmallDown style={{ fontSize: '26px' }} />}
      titleSx={{ flexGrow: 1, maxWidth: '265px' }}
      title={accordionTitleComponent}
    >
      <Stack>
        <PropertiesLabel i18nkey='interface:simulation.simulation_edit.vehicle_types.use_range'>
          <Box sx={{ textAlign: 'right' }}>
            <Switch
              value={isRange}
              checked={isRange}
              disabled={activeRange.maxCapacity <= 1}
              onChange={onVehicleRangeToggle}
            />
          </Box>
        </PropertiesLabel>
      </Stack>
      {isRange && (
        <Stack flexDirection='column' width='100%' gap={1} mt={0.5} mb={4}>
          <Stack direction='row' justifyContent='space-between' gap={2}>
            <PropertiesLabel i18nkey='interface:simulation.simulation_edit.vehicle_types.range.from'>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                spacing={1}
                width='100%'
              >
                <NumberInput
                  value={activeRange.range.from}
                  sx={{ width: '86px' }}
                  minValue={1}
                  onChange={onRangeFromChange}
                  decimals={0}
                  errorType='floating'
                />
              </Stack>
            </PropertiesLabel>
            <PropertiesLabel i18nkey='interface:simulation.simulation_edit.vehicle_types.range.to'>
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                spacing={1}
                width='100%'
              >
                <NumberInput
                  value={activeRange.range.to}
                  sx={{ width: '86px' }}
                  minValue={1}
                  onChange={onRangeToChange}
                  decimals={0}
                  errorType='floating'
                />
              </Stack>
            </PropertiesLabel>
          </Stack>
          <Stack width='33%'>
            <PropertiesLabel
              i18nkey='interface:simulation.simulation_edit.vehicle_types.range.step'
              labelRatio={70}
            >
              <Stack
                direction='row'
                justifyContent='space-between'
                alignItems='center'
                spacing={1}
                width='100%'
              >
                <NumberInput
                  value={activeRange.range.step}
                  sx={{ width: '86px' }}
                  minValue={1}
                  onChange={onStepChange}
                  decimals={0}
                  errorType='floating'
                />
              </Stack>
            </PropertiesLabel>
          </Stack>
        </Stack>
      )}
    </Accordion>
  );
};

export const VehicleType = memo(VehicleTypeComponent);
